import React, { useState, useEffect } from 'react';
import type { MultiValue } from 'react-select';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { GetTeamDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { teamName } from '../../../utils/teamName';

import { Option, Selector } from './Selector';

export interface TeamAllSelectorProps {
  readOnly?: boolean;
  value?: string | string[];
  onChange?: (option: Option | MultiValue<Option> | null | undefined) => void;
  includeAssignedInactiveTeams?: boolean;
}

export default (props: TeamAllSelectorProps) => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const [assignedInactiveTeams, setAssignedInactiveTeams] = useState<
    GetTeamDto[]
  >([]);

  useEffect(() => {
    let mounted = true;

    if (!props.includeAssignedInactiveTeams || !props.value) {
      setAssignedInactiveTeams([]);
      return;
    }

    const assignedIds = Array.isArray(props.value)
      ? props.value
      : [props.value];

    Promise.all(assignedIds.map(id => pok.teams.getById(id).catch(() => null)))
      .then(teams => {
        if (mounted) {
          setAssignedInactiveTeams(
            teams.filter((team): team is GetTeamDto => !!team && !team.active),
          );
        }
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pok.teams, props.includeAssignedInactiveTeams, props.value]);

  const provider = async (text: string) => {
    const activeOptions = await pok.teams.getAllActive(text);
    const matchingInactiveOptions = assignedInactiveTeams
      .filter(team =>
        team.name?.toLocaleLowerCase().includes(text.toLocaleLowerCase()),
      )
      .map(team => ({
        value: team.id,
        label: teamName(team, team.company?.name),
      }));

    return activeOptions.concat(matchingInactiveOptions);
  };

  return (
    <Selector
      uniqueKey={
        'teams-' +
        JSON.stringify(props.value) +
        '-' +
        assignedInactiveTeams.map(team => team.id).join('_')
      }
      value={props.value}
      readOnly={props.readOnly}
      deletedValueName={id => pok.teams.getOptionById(id)}
      isMulti={true}
      creatable={false}
      provider={provider}
      onChange={option => props.onChange && props.onChange(option as Option)}
    />
  );
};
